import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Button, Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerHeader, DrawerOverlay,
  Flex, Heading, HStack, Icon, IconButton, Input, SimpleGrid, Text, VStack, useDisclosure, useToast
} from "@chakra-ui/react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  PiListChecksDuotone, PiPlusBold, PiBroadcastDuotone, PiClockDuotone, PiCheckCircleDuotone, PiNotePencilDuotone,
  PiGlobeDuotone, PiLockDuotone, PiUserPlusDuotone, PiListDuotone, PiShieldCheckDuotone, PiShieldDuotone,
  PiWarningCircleDuotone, PiXCircleDuotone, PiCalendarBlankDuotone, PiUsersThreeDuotone, PiCheckSquareDuotone,
  PiQuestionDuotone, PiTrashDuotone, PiChartBarDuotone, PiDownloadSimpleDuotone, PiArrowRightBold, PiXBold,
  PiFunnelDuotone, PiMagnifyingGlassDuotone, PiLightningDuotone
} from "react-icons/pi";
import {
  ApiVotingListItem,
  Command,
  ParticipationItem,
  VOTING_STATUSES,
  VotingDraftDetails,
  deleteVotingDraft,
  getOrganizerVotings,
  getParticipations,
  getPublicVotings,
  getVotingDetails,
  htmlToPlainText,
  sendVotingProtocole
} from "@lubbezposrednio-frontend/core";
import { CustomConfirmModal } from "@lubbezposrednio-frontend/commons";
import { VotingTile } from "../../components/VotingTile";
import { createVotingModel } from "../CreateVotingView/CreateVotingViewModel";
import { GLASS, SCROLL_FOG, drgba, EASE } from "../../components/glassUi";
import { VotingsGridSkeleton } from "../../components/skeletons";

type Tab = "udzial" | "autorskie" | "odkrywaj";
const TABS: { key: Tab; label: string }[] = [
  { key: "udzial", label: "Biorę udział" },
  { key: "autorskie", label: "Zarządzasz" },
  { key: "odkrywaj", label: "Odkrywaj" }
];
const SUBTITLE: Record<Tab, string> = {
  udzial: "Głosowania, do których jesteś uprawniony jako głosujący.",
  autorskie: "Głosowania, które stworzyłeś — zarządzaj i sprawdzaj wyniki.",
  odkrywaj: "Publiczne głosowania — zapisz się, zagłosuj albo zobacz, co się szykuje."
};

const STATUS_META: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  [VOTING_STATUSES.ONGOING]: { label: "Trwające", color: "#1f9e18", icon: PiBroadcastDuotone },
  [VOTING_STATUSES.PLANNED]: { label: "Planowane", color: "#7b2ff7", icon: PiClockDuotone },
  [VOTING_STATUSES.FINISHED]: { label: "Zakończone", color: "#2563eb", icon: PiCheckCircleDuotone },
  [VOTING_STATUSES.DRAFT]: { label: "Robocze", color: "#8a8397", icon: PiNotePencilDuotone }
};

const plDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString("pl-PL", { day: "2-digit", month: "short", year: "numeric" }) : "");

const TAB_ICON: Record<Tab, React.ElementType> = {
  udzial: PiCheckSquareDuotone,
  autorskie: PiListChecksDuotone,
  odkrywaj: PiGlobeDuotone
};

// ── Nagłówek: czysty glass + pigułki-taby (spójnie z resztą panelu) ──
const HubHeader: React.FC<{ tab: Tab; onTab: (t: Tab) => void }> = ({ tab, onTab }) => (
  <Box borderRadius="24px" sx={GLASS}>
    <Box px={{ base: "18px", md: "26px" }} pt={{ base: "18px", md: "22px" }} pb="14px">
      <Flex align={{ base: "flex-start", sm: "center" }} justify="space-between" gap="14px" direction={{ base: "column", sm: "row" }}>
        <HStack spacing="14px" minW={0}>
          <Flex w={{ base: "50px", md: "56px" }} h={{ base: "50px", md: "56px" }} borderRadius="16px" bg={drgba("#7b2ff7", 0.12)} align="center" justify="center" flexShrink={0}>
            <Icon as={PiListChecksDuotone} boxSize="28px" color="#7b2ff7" />
          </Flex>
          <Box minW={0}>
            <Heading fontSize={{ base: "24px", md: "28px" }} fontWeight="900" color="#1b1030" lineHeight="1.05" letterSpacing="-0.02em">Głosowania</Heading>
            <Text fontSize="13.5px" color="#6b6480" mt="3px" noOfLines={1}>{SUBTITLE[tab]}</Text>
          </Box>
        </HStack>
        <Button as={Link} to="/organizator/utworz-glosowanie" leftIcon={<Icon as={PiPlusBold} />} bg="primary" color="white" borderRadius="12px" fontWeight="800" px="18px" size="sm" flexShrink={0} boxShadow="0 12px 26px -12px rgba(67,0,152,0.6)" _hover={{ bg: "#360079", textDecoration: "none" }}>
          Nowe głosowanie
        </Button>
      </Flex>

      <HStack spacing="3px" mt="16px" pt="12px" borderTop="1px solid rgba(24,12,48,0.07)" overflowX="auto" sx={{ scrollbarWidth: "none", "::-webkit-scrollbar": { display: "none" } }}>
        {TABS.map(t => {
          const active = t.key === tab;
          return (
            <Button key={t.key} onClick={() => onTab(t.key)} leftIcon={<Icon as={TAB_ICON[t.key]} boxSize="15px" />} size="sm" borderRadius="full" fontWeight="800" px="15px" color={active ? "#7b2ff7" : "#6b6480"} bg={active ? "rgba(123,47,247,0.1)" : "transparent"} boxShadow="none" _hover={{ bg: active ? "rgba(123,47,247,0.16)" : "rgba(24,12,48,0.06)" }} flexShrink={0}>
              {t.label}
            </Button>
          );
        })}
      </HStack>
    </Box>
  </Box>
);

const EmptyState: React.FC<{ title: string; text?: string }> = ({ title, text }) => (
  <Flex direction="column" align="center" justify="center" py="56px" borderRadius="22px" sx={GLASS}>
    <Flex w="58px" h="58px" borderRadius="16px" bg={drgba("#430098", 0.1)} align="center" justify="center" mb="14px">
      <Icon as={PiListChecksDuotone} boxSize="28px" color="primary" />
    </Flex>
    <Text color="#241536" fontWeight="900" fontSize="18px" mb="3px" letterSpacing="-0.01em">{title}</Text>
    {text && <Text color="#6b6480" fontSize="14px" textAlign="center" maxW="360px">{text}</Text>}
  </Flex>
);

// ── Pasek „wymaga akcji" pod headerem (Biorę udział / Autorskie) ──
const ActionStrip: React.FC<{ tab: Tab; items: SideItem[]; onResumeDraft: (id: string) => void }> = ({ tab, items, onResumeDraft }) => {
  const urgent =
    tab === "udzial"
      ? items.filter(i => i.status === VOTING_STATUSES.ONGOING && i.voted === false)
      : items.filter(i => i.status === VOTING_STATUSES.ONGOING || i.status === VOTING_STATUSES.DRAFT);
  if (urgent.length === 0) return null;
  const heading = tab === "udzial" ? "Czekają na Twój głos" : "Wymaga Twojej uwagi";
  return (
    <Box borderRadius="20px" px={{ base: "14px", md: "18px" }} py="14px" sx={GLASS}>
      <HStack spacing="8px" mb="12px">
        <Icon as={PiLightningDuotone} boxSize="17px" color="#e51f44" />
        <Text fontSize="12px" fontWeight="900" letterSpacing="0.04em" textTransform="uppercase" color="#5a5468">{heading}</Text>
        <Flex bg="rgba(229,31,68,0.1)" color="#e51f44" px="8px" py="1px" borderRadius="full" fontSize="12px" fontWeight="800">{urgent.length}</Flex>
      </HStack>
      <HStack spacing={{ base: "14px", md: "18px" }} align="flex-start" overflowX="auto" sx={{ scrollbarWidth: "none", "::-webkit-scrollbar": { display: "none" } }}>
        {urgent.map(i => {
          const m = STATUS_META[i.status ?? VOTING_STATUSES.DRAFT] || STATUS_META[VOTING_STATUSES.DRAFT];
          const isDraft = i.status === VOTING_STATUSES.DRAFT;
          const inner = (
            <VStack spacing="7px" w="72px" flexShrink={0} _hover={{ textDecoration: "none" }}>
              <Flex w="54px" h="54px" borderRadius="full" align="center" justify="center" bg={drgba(m.color, 0.12)} border="2px solid" borderColor={drgba(m.color, 0.3)} transition={`all ${EASE}`} _groupHover={{ transform: "translateY(-2px)", borderColor: m.color }}>
                <Icon as={m.icon} boxSize="24px" color={m.color} />
              </Flex>
              <Text fontSize="11.5px" fontWeight="700" color="#3f3a4d" noOfLines={2} textAlign="center" lineHeight="1.2" w="72px">{i.title || "Bez tytułu"}</Text>
            </VStack>
          );
          return isDraft ? (
            <Box key={i.id} as="button" type="button" role="group" onClick={() => onResumeDraft(i.id)}>{inner}</Box>
          ) : (
            <Box key={i.id} as={Link} role="group" to={tab === "udzial" ? `/glosowanie/${i.id}` : `/organizator/glosowanie/${i.id}`}>{inner}</Box>
          );
        })}
      </HStack>
    </Box>
  );
};

// ── Facety filtrów (logika bez zmian) ──
type SideItem = {
  id: string;
  status?: string;
  end_date?: string | null;
  title?: string | null;
  voted?: boolean;
  public?: boolean;
  electorate_type?: string;
  requires_verification?: boolean;
  signup_open?: boolean;
};
type Facets = {
  status?: string;
  voted: "all" | "voted" | "notvoted" | "missed";
  publicity: "all" | "public" | "private";
  electorate: "all" | "closed" | "signup" | "open";
  verification: "all" | "yes" | "no";
  signup: "all" | "open";
};
const DEFAULT_FACETS: Facets = { status: undefined, voted: "all", publicity: "all", electorate: "all", verification: "all", signup: "all" };
const facetsActive = (f: Facets) =>
  !!f.status || f.voted !== "all" || f.publicity !== "all" || f.electorate !== "all" || f.verification !== "all" || f.signup !== "all";

type FacetFields = { status?: string; voted?: boolean; public?: boolean; electorate_type?: string; requires_verification?: boolean; signup_open?: boolean };
const matchFacets = (i: FacetFields, f: Facets) =>
  (f.status ? i.status === f.status : true) &&
  (f.voted === "all" ||
    (f.voted === "voted"
      ? i.voted === true
      : f.voted === "missed"
        ? i.voted === false && i.status === VOTING_STATUSES.FINISHED
        : i.voted === false && i.status !== VOTING_STATUSES.FINISHED)) &&
  (f.publicity === "all" || (f.publicity === "public" ? i.public === true : i.public === false)) &&
  (f.electorate === "all" || i.electorate_type === f.electorate) &&
  (f.verification === "all" || (f.verification === "yes" ? i.requires_verification === true : i.requires_verification === false)) &&
  (f.signup === "all" || i.signup_open === true);

const ELECTORATE_META: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  closed: { label: "Lista imienna", icon: PiListDuotone, color: "#2563eb" },
  signup: { label: "Z zapisami", icon: PiUserPlusDuotone, color: "#7c3aed" },
  open: { label: "Otwarte (referendum)", icon: PiGlobeDuotone, color: "#1f9e18" }
};

const FilterRow: React.FC<{ label: string; icon?: React.ElementType; color?: string; active: boolean; count: number; onClick: () => void }> = ({ label, icon, color, active, count, onClick }) => (
  <HStack as="button" type="button" onClick={onClick} justify="space-between" px="9px" py="7px" borderRadius="10px" bg={active ? drgba(color || "#430098", 0.1) : "transparent"} _hover={{ bg: active ? drgba(color || "#430098", 0.12) : "rgba(24,12,48,0.04)" }} transition={`background ${EASE}`}>
    <HStack spacing="9px" minW={0}>
      {icon && <Icon as={icon} boxSize="15px" color={color || "#a8a2b6"} />}
      <Text fontSize="13.5px" fontWeight={active ? "800" : "600"} color={active ? "#241536" : "#5a5468"} noOfLines={1}>{label}</Text>
    </HStack>
    <Text fontSize="11.5px" fontWeight="800" color={active ? (color || "primary") : "#a8a2b6"} bg={active ? "rgba(255,255,255,0.7)" : "rgba(24,12,48,0.05)"} px="8px" borderRadius="full" minW="24px" textAlign="center" flexShrink={0}>{count}</Text>
  </HStack>
);

// Sekcja rail: nagłówek + zawartość, z górnym separatorem (poza pierwszą)
const RailGroup: React.FC<{ icon: React.ElementType; title: string; color?: string; first?: boolean; children: React.ReactNode }> = ({ icon, title, color = "#430098", first, children }) => (
  <>
    {!first && <Box borderTop="1px solid rgba(67,0,152,0.08)" my="16px" />}
    <Flex align="center" mb="9px">
      <HStack spacing="8px">
        <Icon as={icon} boxSize="17px" color={color} />
        <Text fontSize="12px" fontWeight="800" color="#5a5468" textTransform="uppercase" letterSpacing="0.07em">{title}</Text>
      </HStack>
    </Flex>
    {children}
  </>
);

// ── Rail: filtry (facety) + kończące się ──
const RailFilters: React.FC<{
  tab: Tab;
  items: SideItem[];
  statuses: string[];
  facets: Facets;
  onFacet: (patch: Partial<Facets>) => void;
}> = ({ tab, items, statuses, facets, onFacet }) => {
  const count = (pred: (i: SideItem) => boolean) => items.filter(pred).length;
  const statusCounts = useMemo(() => {
    const m: Record<string, number> = {};
    items.forEach(i => { if (i.status) m[i.status] = (m[i.status] ?? 0) + 1; });
    return m;
  }, [items]);

  const endingSoon = useMemo(() => {
    const now = Date.now();
    const soon = now + 7 * 24 * 3600 * 1000;
    return items
      .filter(i => i.status === VOTING_STATUSES.ONGOING && i.end_date && new Date(i.end_date).getTime() <= soon && new Date(i.end_date).getTime() >= now)
      .sort((a, b) => (a.end_date! < b.end_date! ? -1 : 1))
      .slice(0, 3);
  }, [items]);

  const showStatus = tab !== "odkrywaj";
  const showVoted = tab === "udzial";
  const showPublicity = tab !== "odkrywaj";
  const showSignup = tab !== "udzial";
  const toggle = <K extends keyof Facets>(key: K, val: Facets[K], off: Facets[K]) => onFacet({ [key]: facets[key] === val ? off : val } as Partial<Facets>);

  const summary =
    tab === "udzial"
      ? [
          { label: "Do oddania", value: count(i => i.status === VOTING_STATUSES.ONGOING && i.voted === false), color: "#c41a3a", icon: PiWarningCircleDuotone },
          { label: "Trwające", value: count(i => i.status === VOTING_STATUSES.ONGOING), color: "#1f9e18", icon: PiBroadcastDuotone },
          { label: "Zagłosowane", value: count(i => i.voted === true), color: "#2563eb", icon: PiCheckCircleDuotone }
        ]
      : tab === "autorskie"
        ? [
            { label: "Trwające", value: count(i => i.status === VOTING_STATUSES.ONGOING), color: "#1f9e18", icon: PiBroadcastDuotone },
            { label: "Robocze", value: count(i => i.status === VOTING_STATUSES.DRAFT), color: "#8a8397", icon: PiNotePencilDuotone },
            { label: "Zakończone", value: count(i => i.status === VOTING_STATUSES.FINISHED), color: "#2563eb", icon: PiCheckCircleDuotone }
          ]
        : [
            { label: "Trwające", value: count(i => i.status === VOTING_STATUSES.ONGOING), color: "#1f9e18", icon: PiBroadcastDuotone },
            { label: "Otwarte zapisy", value: count(i => i.signup_open === true), color: "#7c3aed", icon: PiUserPlusDuotone },
            { label: "Planowane", value: count(i => i.status === VOTING_STATUSES.PLANNED), color: "#2563eb", icon: PiClockDuotone }
          ];

  return (
    <>
      {/* Mini-podsumowanie */}
      <Text fontSize="12px" fontWeight="800" color="#5a5468" textTransform="uppercase" letterSpacing="0.08em" mb="11px">Podsumowanie</Text>
      <VStack align="stretch" spacing="10px">
        {summary.map(s => (
          <HStack key={s.label} justify="space-between" spacing="10px">
            <HStack spacing="8px" color="#6b6480" minW={0}><Icon as={s.icon} boxSize="14px" color={s.color} flexShrink={0} /><Text fontSize="13px" noOfLines={1}>{s.label}</Text></HStack>
            <Text fontSize="13px" fontWeight="800" color="#241536" flexShrink={0}>{s.value}</Text>
          </HStack>
        ))}
      </VStack>
      <Box borderTop="1px solid rgba(67,0,152,0.08)" my="18px" />

      <Flex justify="space-between" align="center" mb="14px">
        <HStack spacing="8px">
          <Icon as={PiFunnelDuotone} boxSize="18px" color="#430098" />
          <Text fontSize="13px" fontWeight="900" color="#241536" letterSpacing="-0.01em">Filtry</Text>
        </HStack>
        {facetsActive(facets) && (
          <HStack as="button" type="button" onClick={() => onFacet(DEFAULT_FACETS)} spacing="4px" color="accent" cursor="pointer" _hover={{ opacity: 0.65 }}>
            <Icon as={PiXBold} boxSize="11px" />
            <Text fontSize="12.5px" fontWeight="800">Wyczyść</Text>
          </HStack>
        )}
      </Flex>

      {showStatus && (
        <RailGroup icon={PiListChecksDuotone} title="Status" color="#2563eb" first>
          <VStack align="stretch" spacing="2px">
            <FilterRow label="Wszystkie" active={!facets.status} count={items.length} onClick={() => onFacet({ status: undefined })} />
            {statuses.map(s => (
              <FilterRow key={s} label={STATUS_META[s].label} icon={STATUS_META[s].icon} color={STATUS_META[s].color} active={facets.status === s} count={statusCounts[s] ?? 0} onClick={() => toggle("status", s, undefined)} />
            ))}
          </VStack>
        </RailGroup>
      )}

      {showVoted && (
        <RailGroup icon={PiCheckCircleDuotone} title="Twój głos" color="#1f9e18">
          <VStack align="stretch" spacing="2px">
            <FilterRow label="Wszystkie" active={facets.voted === "all"} count={items.length} onClick={() => onFacet({ voted: "all" })} />
            <FilterRow label="Zagłosowano" icon={PiCheckCircleDuotone} color="#1f9e18" active={facets.voted === "voted"} count={count(i => i.voted === true)} onClick={() => toggle("voted", "voted", "all")} />
            <FilterRow label="Do oddania" icon={PiWarningCircleDuotone} color="#c41a3a" active={facets.voted === "notvoted"} count={count(i => i.voted === false && i.status !== VOTING_STATUSES.FINISHED)} onClick={() => toggle("voted", "notvoted", "all")} />
            <FilterRow label="Nie oddano" icon={PiXCircleDuotone} color="#8a8397" active={facets.voted === "missed"} count={count(i => i.voted === false && i.status === VOTING_STATUSES.FINISHED)} onClick={() => toggle("voted", "missed", "all")} />
          </VStack>
        </RailGroup>
      )}

      {showPublicity && (
        <RailGroup icon={PiGlobeDuotone} title="Jawność" color="#1f9e18">
          <VStack align="stretch" spacing="2px">
            <FilterRow label="Wszystkie" active={facets.publicity === "all"} count={items.length} onClick={() => onFacet({ publicity: "all" })} />
            <FilterRow label="Publiczne" icon={PiGlobeDuotone} color="#1f9e18" active={facets.publicity === "public"} count={count(i => i.public === true)} onClick={() => toggle("publicity", "public", "all")} />
            <FilterRow label="Niepubliczne" icon={PiLockDuotone} color="#8a8397" active={facets.publicity === "private"} count={count(i => i.public === false)} onClick={() => toggle("publicity", "private", "all")} />
          </VStack>
        </RailGroup>
      )}

      {showSignup && (
        <RailGroup icon={PiUserPlusDuotone} title="Zapisy" color="#7c3aed">
          <VStack align="stretch" spacing="2px">
            <FilterRow label="Wszystkie" active={facets.signup === "all"} count={items.length} onClick={() => onFacet({ signup: "all" })} />
            <FilterRow label="Otwarte zapisy" icon={PiUserPlusDuotone} color="#7c3aed" active={facets.signup === "open"} count={count(i => i.signup_open === true)} onClick={() => toggle("signup", "open", "all")} />
          </VStack>
        </RailGroup>
      )}

      <RailGroup icon={PiUsersThreeDuotone} title="Typ elektoratu" color="#2563eb">
        <VStack align="stretch" spacing="2px">
          <FilterRow label="Wszystkie" active={facets.electorate === "all"} count={items.length} onClick={() => onFacet({ electorate: "all" })} />
          {(["closed", "signup", "open"] as const).map(e => (
            <FilterRow key={e} label={ELECTORATE_META[e].label} icon={ELECTORATE_META[e].icon} color={ELECTORATE_META[e].color} active={facets.electorate === e} count={count(i => i.electorate_type === e)} onClick={() => toggle("electorate", e, "all")} />
          ))}
        </VStack>
      </RailGroup>

      <RailGroup icon={PiShieldCheckDuotone} title="Weryfikacja tożsamości" color="#1f9e18">
        <VStack align="stretch" spacing="2px">
          <FilterRow label="Wszystkie" active={facets.verification === "all"} count={items.length} onClick={() => onFacet({ verification: "all" })} />
          <FilterRow label="Wymaga weryfikacji" icon={PiShieldCheckDuotone} color="#1f9e18" active={facets.verification === "yes"} count={count(i => i.requires_verification === true)} onClick={() => toggle("verification", "yes", "all")} />
          <FilterRow label="Bez weryfikacji" icon={PiShieldDuotone} color="#8a8397" active={facets.verification === "no"} count={count(i => i.requires_verification === false)} onClick={() => toggle("verification", "no", "all")} />
        </VStack>
      </RailGroup>

      {tab !== "odkrywaj" && endingSoon.length > 0 && (
        <RailGroup icon={PiClockDuotone} title="Kończą się wkrótce" color="#e51f44">
          <VStack align="stretch" spacing="2px">
            {endingSoon.map(v => (
              <HStack key={v.id} as={Link} to={`/organizator/glosowanie/${v.id}`} justify="space-between" py="7px" px="8px" borderRadius="11px" transition={`background ${EASE}`} _hover={{ bg: "rgba(24,12,48,0.04)", textDecoration: "none" }}>
                <HStack spacing="9px" minW={0}>
                  <Box w="7px" h="7px" borderRadius="full" bg="#1f9e18" flexShrink={0} />
                  <Text fontSize="13px" fontWeight="600" color="#2f2a3d" noOfLines={1}>{v.title}</Text>
                </HStack>
                <Text fontSize="10.5px" fontWeight="800" color="#a8a2b6" flexShrink={0}>{plDate(v.end_date)}</Text>
              </HStack>
            ))}
          </VStack>
        </RailGroup>
      )}
    </>
  );
};

// ── Biorę udział ──
const UdzialPanel: React.FC<{ facets: Facets; query: string; onData: (i: SideItem[]) => void }> = ({ facets, query, onData }) => {
  const [votings, setVotings] = useState<ParticipationItem[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    getParticipations().then(r => { const v = r.data?.votings ?? []; setVotings(v); onData(v.map(x => ({ id: x.id, status: x.status, end_date: x.end_date, title: x.title, voted: x.voted, public: x.public, electorate_type: x.electorate_type, requires_verification: x.requires_verification }))); }).catch(() => undefined).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);
  const q = query.trim().toLowerCase();
  const shown = votings
    .filter(v => matchFacets(v, facets) && (!q || (v.title || "").toLowerCase().includes(q)))
    .sort((a, b) => {
      const pa = a.status === VOTING_STATUSES.ONGOING && a.voted === false ? 0 : 1;
      const pb = b.status === VOTING_STATUSES.ONGOING && b.voted === false ? 0 : 1;
      return pa - pb;
    });
  if (loading) return <VotingsGridSkeleton />;
  if (!votings.length) return <EmptyState title="Nie bierzesz jeszcze udziału w żadnym głosowaniu" text="Zaproszenia do głosowań trafiają na Twój e-mail." />;
  if (!shown.length) return <EmptyState title="Brak głosowań dla tego filtra" />;
  return (
    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="16px">
      {shown.map(v => (
        <VotingTile key={v.id} id={v.id} title={v.title} status={v.status} startDate={v.start_date} endDate={v.end_date} voted={v.voted} protocolUrl={v.protocol_url} votersCount={v.voters_count} votesCount={v.votes_count} questionsCount={v.questions_count} questionPreview={v.question_preview} organizer={v.organizer ? { name: v.organizer.name, avatarUrl: v.organizer.avatar_url, profileRef: v.organizer.profile_ref } : null} />
      ))}
    </SimpleGrid>
  );
};

// ── Autorskie (zarządzanie) ──
const AutorskieCard: React.FC<{ v: ApiVotingListItem; onDeleted: (id: string) => void }> = ({ v, onDeleted }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const [confirm, setConfirm] = useState(false);
  const [protoLoading, setProtoLoading] = useState(false);
  const status = v.status ?? VOTING_STATUSES.DRAFT;
  const m = STATUS_META[status];
  const isDraft = status === VOTING_STATUSES.DRAFT;
  const isFinished = status === VOTING_STATUSES.FINISHED;
  const turnout = v.voters_count && v.voters_count > 0 ? Math.round(((v.votes_count ?? 0) / v.voters_count) * 100) : null;
  const dates = [v.start_date, v.end_date].filter(Boolean).map(plDate).join(" – ");

  const continueDraft = async () => {
    try {
      const r = await getVotingDetails(v.id);
      const d: any = r.data?.voting;
      if (!d) throw new Error();
      const backend = process.env.REACT_APP_BACKEND_URL;
      const draft: VotingDraftDetails = {
        id: d.id,
        name: d.title,
        startDate: d.start_date,
        endDate: d.end_date,
        questions: (d.questions ?? []).map((q: any) => ({ id: q.id, question: q.content, options: q.answers.map((a: any) => htmlToPlainText(a.content)) })),
        privacyPolicyUrl: d.privacy_policy_url ? backend + d.privacy_policy_url : undefined,
        termsAndConditionsUrl: d.terms_and_conditions_url ? backend + d.terms_and_conditions_url : undefined,
        emails: d.emails ?? undefined,
        votersListUrl: d.voter_list_url ? backend + d.voter_list_url : undefined,
        isPublic: d.public
      };
      createVotingModel.loadDataFromDraft(draft);
      navigate("/organizator/utworz-glosowanie");
    } catch {
      toast({ title: "Nie udało się otworzyć wersji roboczej.", status: "error", isClosable: true });
    }
  };

  const sendProtocol = async () => {
    setProtoLoading(true);
    try {
      const r = await sendVotingProtocole(v.id);
      toast({ description: r?.status === 202 ? "Wiadomość e-mail z protokołem zostanie przesłana." : "Protokół nie został jeszcze wygenerowany. Spróbuj za chwilę.", status: r?.status === 202 ? "success" : "error", isClosable: true });
    } catch {
      toast({ description: "Wystąpił błąd podczas przesyłania protokołu.", status: "error", isClosable: true });
    } finally {
      setProtoLoading(false);
    }
  };

  const deleteCommand = useMemo(
    () =>
      new Command(async () => {
        try {
          const r = await deleteVotingDraft(v.id);
          if (r?.status === 204) { toast({ title: "Wersja robocza została usunięta", status: "success", isClosable: true }); onDeleted(v.id); }
          else toast({ title: "Nie udało się usunąć", status: "error", isClosable: true });
        } catch {
          toast({ title: "Nie udało się usunąć", status: "error", isClosable: true });
        } finally {
          setConfirm(false);
        }
      }),
    // eslint-disable-next-line
    [v.id]
  );

  const Stat: React.FC<{ icon: React.ElementType; value: number; label: string; accent?: boolean }> = ({ icon, value, label, accent }) => (
    <Flex direction="column" align="center" bg={drgba("#430098", 0.05)} borderRadius="12px" py="10px">
      <HStack spacing="5px"><Icon as={icon} color="primary" boxSize="15px" /><Text fontWeight="800" color="#241536" fontSize="16px" lineHeight="1">{value}</Text></HStack>
      <Text fontSize="11px" color={accent ? "accent" : "#6b6480"} fontWeight={accent ? "800" : "500"} mt="3px">{label}</Text>
    </Flex>
  );

  return (
    <Flex direction="column" borderRadius="20px" p="20px" gap="14px" sx={GLASS} transition={`transform ${EASE}, box-shadow ${EASE}`} _hover={{ transform: "translateY(-3px)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), 0 22px 50px -22px rgba(43,0,90,0.5)" }}>
      <HStack justify="space-between">
        <HStack spacing="6px" bg={drgba(m.color, 0.12)} color={m.color} px="10px" py="4px" borderRadius="full"><Icon as={m.icon} boxSize="14px" /><Text fontSize="12px" fontWeight="800">{m.label}</Text></HStack>
        <HStack spacing="5px" color="#a8a2b6"><Icon as={v.public ? PiGlobeDuotone : PiLockDuotone} boxSize="14px" /><Text fontSize="12px" fontWeight="700">{v.public ? "Jawne" : "Niepubliczne"}</Text></HStack>
      </HStack>

      <Box minH="48px">
        {isDraft ? (
          <Heading fontSize="18px" fontWeight="800" lineHeight="1.3" color="#241536" noOfLines={2}>{v.title || "Bez tytułu"}</Heading>
        ) : (
          <Heading as={Link} to={`/organizator/glosowanie/${v.id}`} fontSize="18px" fontWeight="800" lineHeight="1.3" color="#241536" noOfLines={2} display="block" _hover={{ color: "accent" }}>{v.title}</Heading>
        )}
      </Box>

      {dates && <HStack spacing="7px" color="#6b6480" fontSize="13px"><Icon as={PiCalendarBlankDuotone} boxSize="15px" /><Text noOfLines={1}>{dates}</Text></HStack>}

      <SimpleGrid columns={3} spacing="8px">
        <Stat icon={PiUsersThreeDuotone} value={v.voters_count ?? 0} label="głosujących" />
        <Stat icon={PiCheckSquareDuotone} value={v.votes_count ?? 0} label={turnout != null ? `${turnout}% frekw.` : "oddanych"} accent={turnout != null} />
        <Stat icon={PiQuestionDuotone} value={v.questions_count ?? 0} label="pytań" />
      </SimpleGrid>

      <Box flex="1" />
      <HStack spacing="8px" w="100%">
        {isDraft && (
          <>
            <Button flex="1" size="sm" variant="outline" borderColor="primary" color="primary" borderRadius="11px" leftIcon={<Icon as={PiNotePencilDuotone} />} onClick={continueDraft} _hover={{ bg: drgba("#430098", 0.06) }}>Dokończ</Button>
            <IconButton aria-label="Usuń wersję roboczą" icon={<Icon as={PiTrashDuotone} />} size="sm" variant="ghost" color="#a8a2b6" borderRadius="11px" onClick={() => setConfirm(true)} _hover={{ color: "accent", bg: "rgba(229,31,68,0.06)" }} />
          </>
        )}
        {isFinished && (
          <>
            <Button flex="1" as={Link} to={`/organizator/glosowanie/${v.id}/wyniki`} size="sm" bg="primary" color="white" borderRadius="11px" leftIcon={<Icon as={PiChartBarDuotone} />} _hover={{ bg: "#36007a" }}>Wyniki</Button>
            <Button size="sm" variant="outline" borderColor="primary" color="primary" borderRadius="11px" leftIcon={<Icon as={PiDownloadSimpleDuotone} />} isLoading={protoLoading} onClick={sendProtocol} _hover={{ bg: drgba("#430098", 0.06) }}>Protokół</Button>
          </>
        )}
        {!isDraft && !isFinished && (
          <Button flex="1" as={Link} to={`/organizator/glosowanie/${v.id}`} size="sm" variant="outline" borderColor="primary" color="primary" borderRadius="11px" rightIcon={<Icon as={PiArrowRightBold} boxSize="13px" />} _hover={{ bg: drgba("#430098", 0.06) }}>Zarządzaj</Button>
        )}
      </HStack>

      <CustomConfirmModal isOpen={confirm} onClose={() => setConfirm(false)} title="Usuń wersję roboczą" description={`Czy na pewno chcesz usunąć wersję roboczą: ${v.title || "bez tytułu"}?`} confirmCommand={deleteCommand} />
    </Flex>
  );
};

const toSide = (x: ApiVotingListItem): SideItem => ({ id: x.id, status: x.status, end_date: x.end_date, title: x.title, public: x.public, electorate_type: x.electorate_type, requires_verification: x.requires_verification, signup_open: x.signup_open });
const AutorskiePanel: React.FC<{ facets: Facets; query: string; onData: (i: SideItem[]) => void }> = ({ facets, query, onData }) => {
  const [votings, setVotings] = useState<ApiVotingListItem[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    getOrganizerVotings({ per_page: 500 }).then(r => { const v = (r.data?.votings ?? []) as ApiVotingListItem[]; setVotings(v); onData(v.map(toSide)); }).catch(() => undefined).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);
  const onDeleted = (id: string) => setVotings(prev => { const next = prev.filter(x => x.id !== id); onData(next.map(toSide)); return next; });
  const q = query.trim().toLowerCase();
  const shown = votings.filter(v => matchFacets(v, facets) && (!q || (v.title || "").toLowerCase().includes(q)));
  if (loading) return <VotingsGridSkeleton />;
  if (!votings.length) return <EmptyState title="Nie masz jeszcze żadnych głosowań" text="Utwórz pierwsze weryfikowalne głosowanie." />;
  if (!shown.length) return <EmptyState title="Brak głosowań dla tego filtra" />;
  return (
    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="16px">
      {shown.map(v => <AutorskieCard key={v.id} v={v} onDeleted={onDeleted} />)}
    </SimpleGrid>
  );
};

// ── Odkrywaj (sekcje) ──
const organizerName = (u: ApiVotingListItem["user"]) => (u?.is_organization ? u?.organization_name : `${u?.first_name ?? ""} ${u?.last_name ?? ""}`.trim()) || u?.email || "";
const OdkrywajPanel: React.FC<{ facets: Facets; query: string; onData: (i: SideItem[]) => void }> = ({ facets, query, onData }) => {
  const [votings, setVotings] = useState<ApiVotingListItem[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    getPublicVotings({ per_page: 60, status: [VOTING_STATUSES.ONGOING, VOTING_STATUSES.PLANNED] }).then(r => { const v = (r.data?.votings ?? []) as ApiVotingListItem[]; setVotings(v); onData(v.map(toSide)); }).catch(() => undefined).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);
  const q = query.trim().toLowerCase();
  const filtered = votings.filter(v => matchFacets(v, facets) && (!q || (v.title || "").toLowerCase().includes(q)));
  const zapisy = filtered.filter(v => v.status === VOTING_STATUSES.PLANNED && v.signup_open);
  const trwa = filtered.filter(v => v.status === VOTING_STATUSES.ONGOING);
  const planowane = filtered.filter(v => v.status === VOTING_STATUSES.PLANNED && !v.signup_open);
  const card = (v: ApiVotingListItem, signup: boolean) => {
    const orgLabel = organizerName(v.user);
    return <VotingTile key={v.id} id={v.id} title={v.title} status={v.status} startDate={v.start_date} endDate={v.end_date} votersCount={v.voters_count} votesCount={v.votes_count} questionsCount={v.questions_count} questionPreview={v.question_preview} signupOpen={signup || v.signup_open} signupClosesAt={v.signup_closes_at} organizer={orgLabel ? { name: orgLabel, avatarUrl: v.user?.avatar_url, profileRef: v.user?.profile_ref } : null} />;
  };
  const Section: React.FC<{ title: string; list: ApiVotingListItem[]; signup?: boolean; icon: React.ElementType; color: string }> = ({ title, list, signup, icon, color }) => list.length === 0 ? null : (
    <Box w="100%">
      <HStack mb="14px" spacing="10px">
        <Icon as={icon} boxSize="20px" color={color} />
        <Heading fontSize="19px" color="#241536" letterSpacing="-0.01em">{title}</Heading>
        <Flex bg={drgba(color, 0.12)} color={color} px="9px" py="2px" borderRadius="full" fontSize="13px" fontWeight="800">{list.length}</Flex>
      </HStack>
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="16px">{list.map(v => card(v, !!signup))}</SimpleGrid>
    </Box>
  );
  if (loading) return <VotingsGridSkeleton />;
  if (!zapisy.length && !trwa.length && !planowane.length) return <EmptyState title={facetsActive(facets) ? "Brak głosowań dla tego filtra" : "Brak aktywnych i planowanych głosowań"} />;
  return (
    <VStack align="stretch" spacing="30px">
      <Section title="Zapisy otwarte" list={zapisy} signup icon={PiUserPlusDuotone} color="#7c3aed" />
      <Section title="Trwają" list={trwa} icon={PiBroadcastDuotone} color="#1f9e18" />
      <Section title="Planowane" list={planowane} icon={PiClockDuotone} color="#2563eb" />
    </VStack>
  );
};

// ── Hub ──
const VotingsHubView: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: Tab = (TABS.find(t => t.key === searchParams.get("w"))?.key ?? "udzial") as Tab;
  const setTab = (t: Tab) => setSearchParams(prev => { const p = new URLSearchParams(prev); if (t === "udzial") p.delete("w"); else p.set("w", t); return p; }, { replace: true });

  const [facets, setFacets] = useState<Facets>(DEFAULT_FACETS);
  const patchFacets = (patch: Partial<Facets>) => setFacets(prev => ({ ...prev, ...patch }));
  const [tabData, setTabData] = useState<Record<Tab, SideItem[]>>({ udzial: [], autorskie: [], odkrywaj: [] });
  const setData = (t: Tab) => (i: SideItem[]) => setTabData(prev => ({ ...prev, [t]: i }));

  const lastTab = useRef(tab);
  useEffect(() => { if (lastTab.current !== tab) { lastTab.current = tab; setFacets(DEFAULT_FACETS); } }, [tab]);

  const [visited, setVisited] = useState<Set<Tab>>(() => new Set([tab]));
  useEffect(() => { setVisited(v => (v.has(tab) ? v : new Set(v).add(tab))); }, [tab]);

  useEffect(() => { document.title = "Głosowania"; }, []);

  const statusesFor: Record<Tab, string[]> = {
    udzial: [VOTING_STATUSES.ONGOING, VOTING_STATUSES.PLANNED, VOTING_STATUSES.FINISHED],
    autorskie: [VOTING_STATUSES.ONGOING, VOTING_STATUSES.PLANNED, VOTING_STATUSES.FINISHED, VOTING_STATUSES.DRAFT],
    odkrywaj: []
  };

  const navigate = useNavigate();
  const toast = useToast();
  const [query, setQuery] = useState("");
  const filtersDrawer = useDisclosure();
  useEffect(() => { setQuery(""); }, [tab]);

  // Wznowienie szkicu z paska „wymaga akcji" (ta sama logika co w karcie autorskiej).
  const resumeDraft = async (id: string) => {
    try {
      const r = await getVotingDetails(id);
      const d: any = r.data?.voting;
      if (!d) throw new Error();
      const backend = process.env.REACT_APP_BACKEND_URL;
      createVotingModel.loadDataFromDraft({
        id: d.id,
        name: d.title,
        startDate: d.start_date,
        endDate: d.end_date,
        questions: (d.questions ?? []).map((q: any) => ({ id: q.id, question: q.content, options: q.answers.map((a: any) => htmlToPlainText(a.content)) })),
        privacyPolicyUrl: d.privacy_policy_url ? backend + d.privacy_policy_url : undefined,
        termsAndConditionsUrl: d.terms_and_conditions_url ? backend + d.terms_and_conditions_url : undefined,
        emails: d.emails ?? undefined,
        votersListUrl: d.voter_list_url ? backend + d.voter_list_url : undefined,
        isPublic: d.public
      });
      navigate("/organizator/utworz-glosowanie");
    } catch {
      toast({ title: "Nie udało się otworzyć wersji roboczej.", status: "error", isClosable: true });
    }
  };

  const rail = <RailFilters tab={tab} items={tabData[tab]} statuses={statusesFor[tab]} facets={facets} onFacet={patchFacets} />;
  const mobileStatuses = statusesFor[tab];

  return (
    <Flex align="flex-start">
      <Box flex="1" minW={0}>
        <Box w="100%" maxW="1000px" mx="auto" px={{ base: "20px", md: "44px" }} py={{ base: "24px", md: "36px" }}>
          <VStack align="stretch" spacing="16px">
            <HubHeader tab={tab} onTab={setTab} />

            {tab !== "odkrywaj" && <ActionStrip tab={tab} items={tabData[tab]} onResumeDraft={resumeDraft} />}

            {/* Toolbar: wyszukiwarka + (mobile) Filtry + chipy statusu */}
            <Box borderRadius="18px" px={{ base: "14px", md: "16px" }} py="12px" sx={GLASS}>
              <Flex gap="10px" align="center">
                <HStack flex="1" spacing="10px" bg="rgba(255,255,255,0.7)" border="1px solid rgba(24,12,48,0.08)" borderRadius="12px" px="13px" h="42px" _focusWithin={{ borderColor: drgba("#430098", 0.4), boxShadow: `0 0 0 3px ${drgba("#430098", 0.12)}` }}>
                  <Icon as={PiMagnifyingGlassDuotone} boxSize="18px" color="#9a94ab" />
                  <Input variant="unstyled" placeholder="Szukaj po tytule…" value={query} onChange={e => setQuery(e.target.value)} fontSize="14px" fontWeight="600" />
                  {query && <Icon as={PiXBold} boxSize="12px" color="#9a94ab" cursor="pointer" onClick={() => setQuery("")} />}
                </HStack>
                <Button display={{ base: "inline-flex", xl: "none" }} onClick={filtersDrawer.onOpen} leftIcon={<Icon as={PiFunnelDuotone} boxSize="16px" />} size="md" h="42px" variant="outline" borderColor={facetsActive(facets) ? "#7b2ff7" : "rgba(24,12,48,0.12)"} color={facetsActive(facets) ? "#7b2ff7" : "#5a5468"} bg={facetsActive(facets) ? "rgba(123,47,247,0.08)" : "transparent"} borderRadius="12px" fontWeight="800" flexShrink={0} _hover={{ bg: "rgba(24,12,48,0.04)" }}>
                  Filtry
                </Button>
              </Flex>

              {mobileStatuses.length > 0 && (
                <HStack display={{ base: "flex", xl: "none" }} spacing="7px" mt="11px" overflowX="auto" sx={{ scrollbarWidth: "none", "::-webkit-scrollbar": { display: "none" } }}>
                  {[undefined, ...mobileStatuses].map(s => {
                    const active = facets.status === s;
                    const meta = s ? STATUS_META[s] : null;
                    return (
                      <Button key={s ?? "all"} onClick={() => patchFacets({ status: active && s ? undefined : s })} size="sm" borderRadius="full" fontWeight="800" px="13px" flexShrink={0} boxShadow="none"
                        leftIcon={meta ? <Icon as={meta.icon} boxSize="13px" /> : undefined}
                        color={active ? meta?.color ?? "#7b2ff7" : "#6b6480"} bg={active ? drgba(meta?.color ?? "#7b2ff7", 0.12) : "transparent"}
                        _hover={{ bg: active ? drgba(meta?.color ?? "#7b2ff7", 0.18) : "rgba(24,12,48,0.06)" }}>
                        {s ? meta!.label : "Wszystkie"}
                      </Button>
                    );
                  })}
                </HStack>
              )}
            </Box>

            <Box minW={0}>
              {visited.has("udzial") && <Box display={tab === "udzial" ? "block" : "none"}><UdzialPanel facets={tab === "udzial" ? facets : DEFAULT_FACETS} query={tab === "udzial" ? query : ""} onData={setData("udzial")} /></Box>}
              {visited.has("autorskie") && <Box display={tab === "autorskie" ? "block" : "none"}><AutorskiePanel facets={tab === "autorskie" ? facets : DEFAULT_FACETS} query={tab === "autorskie" ? query : ""} onData={setData("autorskie")} /></Box>}
              {visited.has("odkrywaj") && <Box display={tab === "odkrywaj" ? "block" : "none"}><OdkrywajPanel facets={tab === "odkrywaj" ? facets : DEFAULT_FACETS} query={tab === "odkrywaj" ? query : ""} onData={setData("odkrywaj")} /></Box>}
            </Box>
          </VStack>
        </Box>
      </Box>

      {/* Pływający prawy panel: podsumowanie + filtry (glass + scroll-fog) */}
      <Box as="aside" display={{ base: "none", xl: "block" }} w="384px" flexShrink={0} alignSelf="stretch">
        <Box position="sticky" top="0" h="100vh" p="12px">
          <Box position="relative" h="100%" borderRadius="26px" overflow="hidden" sx={GLASS}>
            <Box position="absolute" inset={0} pointerEvents="none" sx={{ background: "radial-gradient(58% 40% at 50% 20%, rgba(124,58,237,0.14), transparent 72%)" }} />
            <VStack position="relative" align="stretch" spacing="0" h="100%" px="18px" py="22px" sx={{ ...SCROLL_FOG, maskImage: "linear-gradient(to bottom, #000 0, #000 calc(100% - 24px), transparent 100%)", WebkitMaskImage: "linear-gradient(to bottom, #000 0, #000 calc(100% - 24px), transparent 100%)" }}>
              {rail}
            </VStack>
          </Box>
        </Box>
      </Box>

      {/* Mobilny bottom-sheet z filtrami i podsumowaniem */}
      <Drawer isOpen={filtersDrawer.isOpen} placement="bottom" onClose={filtersDrawer.onClose}>
        <DrawerOverlay />
        <DrawerContent borderTopRadius="24px" maxH="82vh" sx={{ width: "min(560px, calc(100% - 16px)) !important", marginInline: "auto" }}>
          <DrawerHeader fontSize="17px" fontWeight="900" color="#241536">Filtry i podsumowanie</DrawerHeader>
          <DrawerCloseButton />
          <DrawerBody pb="24px">{rail}</DrawerBody>
        </DrawerContent>
      </Drawer>
    </Flex>
  );
};

export default VotingsHubView;
